import { Box, Flex, Loader, Select, Table, Text } from "@mantine/core";
import React, { ReactNode } from "react";
import styled from "styled-components";
import CustomPagination from "../CustomPagination";
import CustomSearchInput from "../Inputs/CustomSearchInput";
import useIsMobile from "../../../utils/useIsMobile";
import { media } from "../../../theme";

export interface Column<T = any> {
  key: string;
  header: string;
  /** Kolon genişliği, ör. "20%" */
  width?: string;
  render?: (row: T) => ReactNode;
  /** Verilirse başlık tıklanabilir olur (sıralama için) */
  onHeaderClick?: () => void;
  headerAlign?: "left" | "right" | "center";
  /**
   * Dar ekranda (kart kipi) bu kolonun nereye düşeceği. Verilmezse:
   * ilk kolon `title`, key/header'ı "işlem" olanlar `actions`, kalanı `meta`.
   * - `title`   → kartın başlığı (etiketsiz, kalın)
   * - `meta`    → "Etiket: değer" satırı
   * - `actions` → kartın altındaki eylem şeridi (etiketsiz)
   * - `hide`    → dar ekranda hiç gösterilmez
   */
  mobile?: "title" | "meta" | "actions" | "hide";
}

interface Props<T = any> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyText?: string;
  rowKey?: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Satır bazlı ek stil (ör. silinmiş kayıtları soluklaştırmak) */
  rowStyle?: (row: T) => React.CSSProperties | undefined;

  /** Arama kutusu — verilmezse gösterilmez */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  /** Arama kutusunun sağına eklenen filtreler (select'ler vb.) */
  toolbar?: ReactNode;
  /** Filtre satırının altına eklenen ekstra satır (ör. switch'ler) */
  subToolbar?: ReactNode;

  /**
   * Sayfalama — ÜÇÜ BİRDEN verilmezse alt çubuk hiç basılmaz.
   * Sayfalaması olmayan gömülü listeler (personelin izinleri, ekibin üyeleri)
   * de bu bileşeni kullanabilsin diye isteğe bağlı: aksi hâlde her biri kendi
   * tablosunu yazmak zorunda kalıyordu, kart kipi de onlara gelmiyordu.
   */
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

const PAGE_SIZES = ["5", "10", "25", "50"];

/**
 * Kolonun dar ekrandaki rolü. Kolon `mobile` alanını verdiyse o kazanır;
 * vermediyse yaygın kullanımdan türetilir — böylece 9 çağrı yerinin hiçbirini
 * değiştirmeden kart kipi çalışır.
 */
const mobileRoleOf = (col: Column, index: number) => {
  if (col.mobile) return col.mobile;
  if (index === 0) return "title";
  const header = (col.header ?? "").toLocaleLowerCase("tr");
  // Eylem kolonunun başlığı kartta anlamsız ("İŞLEMLER: [düzenle][sil]").
  if (col.key === "actions" || header === "işlemler" || header.trim() === "") {
    return "actions";
  }
  return "meta";
};

/**
 * Panelin sayfalanan CRUD tabloları için tek görsel kaynak.
 * Stil bloğu daha önce 8 ayrı tabloda kopyalanıyordu.
 */
function DataTable<T = any>({
  columns,
  rows,
  loading = false,
  emptyText = "Kayıt bulunamadı.",
  rowKey,
  onRowClick,
  rowStyle,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Ara...",
  toolbar,
  subToolbar,
  page,
  totalPages,
  onPageChange,
  limit,
  onLimitChange,
}: Props<T>) {
  const hasToolbar = onSearchChange !== undefined || toolbar !== undefined;
  const hasPagination =
    page !== undefined && totalPages !== undefined && onPageChange !== undefined;
  const isMobile = useIsMobile();

  /*
   * NEDEN DAR EKRANDA TABLO DEĞİL KART:
   * StyledTable `table-layout: fixed` + `width: 100%` ve kolonlar yüzde
   * genişlikli. 390px'te "%17" 66px eder — başlıklar da hücreler de harf harf
   * sarıyordu ve `overflow-x: auto` hiç devreye girmiyordu, çünkü tablo zaten
   * tam genişlikteydi. Yatay kaydırma da çözüm değil: 6 kolonluk CRUD
   * listesinde kullanıcı satırın sonunu hiç görmüyor. Kart, satırı dikey
   * okunur hâle getirir.
   */
  const renderCards = () => {
    if (loading) {
      return (
        <StateCell>
          <Loader size="sm" color="#5C26FF" />
        </StateCell>
      );
    }

    if (rows.length === 0) {
      return (
        <StateCell>
          <Text size="md" c="dimmed">
            {emptyText}
          </Text>
        </StateCell>
      );
    }

    return (
      <CardList>
        {rows.map((row: any, index) => {
          const cells = columns.map((col, colIndex) => ({
            col,
            role: mobileRoleOf(col, colIndex),
            content: col.render ? (
              col.render(row)
            ) : (
              <Text fw={400} size="md" style={{ color: "#111827" }}>
                {row?.[col.key] ?? "-"}
              </Text>
            ),
          }));

          const titleCells = cells.filter((c) => c.role === "title");
          const metaCells = cells.filter((c) => c.role === "meta");
          const actionCells = cells.filter((c) => c.role === "actions");

          return (
            <Card
              key={rowKey ? rowKey(row) : row?._id ?? index}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              $clickable={!!onRowClick}
              style={rowStyle?.(row)}
            >
              {titleCells.map((c) => (
                <CardTitle key={c.col.key}>{c.content}</CardTitle>
              ))}

              {metaCells.map((c) => (
                <CardRow key={c.col.key}>
                  <CardLabel>{c.col.header}</CardLabel>
                  <CardValue>{c.content}</CardValue>
                </CardRow>
              ))}

              {actionCells.length > 0 && (
                <CardActions>
                  {actionCells.map((c) => (
                    <React.Fragment key={c.col.key}>{c.content}</React.Fragment>
                  ))}
                </CardActions>
              )}
            </Card>
          );
        })}
      </CardList>
    );
  };

  return (
    <PageContainer>
      <TableCard>
        {hasToolbar && (
          <FilterWrapper>
            {onSearchChange !== undefined ? (
              <CustomSearchInput
                w={240}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(event: any) =>
                  onSearchChange(event.currentTarget.value)
                }
              />
            ) : (
              <span />
            )}
            {toolbar && <Flex gap={8}>{toolbar}</Flex>}
          </FilterWrapper>
        )}

        {subToolbar && <SubToolbarRow>{subToolbar}</SubToolbarRow>}

        {isMobile ? (
          renderCards()
        ) : (
          <Box style={{ width: "100%", overflowX: "auto" }}>
            <StyledTable highlightOnHover>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        width: col.width,
                        textAlign: col.headerAlign,
                        cursor: col.onHeaderClick ? "pointer" : undefined,
                      }}
                      onClick={col.onHeaderClick}
                    >
                      {col.header}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length}>
                    <StateCell>
                      <Loader size="sm" color="#5C26FF" />
                    </StateCell>
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>
                    <StateCell>
                      <Text size="md" c="dimmed">
                        {emptyText}
                      </Text>
                    </StateCell>
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((row: any, index) => (
                  <tr
                    key={rowKey ? rowKey(row) : row?._id ?? index}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{
                      cursor: onRowClick ? "pointer" : undefined,
                      ...(rowStyle?.(row) ?? {}),
                    }}
                  >
                    {columns.map((col) => (
                      <td key={col.key}>
                        {col.render ? (
                          col.render(row)
                        ) : (
                          <Text fw={400} size="md" style={{ color: "#111827" }}>
                            {row?.[col.key] ?? "-"}
                          </Text>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </Box>
        )}
      </TableCard>

      {hasPagination && (
        <FooterRow>
          {onLimitChange && (
            <Select
              w={110}
              size="sm"
              radius={10}
              aria-label="Sayfa boyutu"
              data={PAGE_SIZES}
              value={String(limit)}
              allowDeselect={false}
              onChange={(value) => value && onLimitChange(Number(value))}
            />
          )}
          <CustomPagination
            total={totalPages}
            value={page}
            onChange={onPageChange}
          />
          {onLimitChange && <Spacer />}
        </FooterRow>
      )}
    </PageContainer>
  );
}

export default DataTable;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.01);
  overflow: hidden;
  width: 100%;
`;

const FilterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;
  flex-wrap: wrap;

  /*
   * Arama kutusu ve filtreler sabit genişlikli (w=240 / w=190) — dar ekranda
   * yan yana sığmayıp taşıyorlardı. !important şart: Mantine w prop'unu satır
   * içi stil olarak basıyor, satır içi stili ancak !important geçebiliyor.
   */
  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;

    /* Arama kutusu tam genişlik. */
    > * {
      width: 100% !important;
    }

    /* Filtre kabındaki select'ler satır başına iki tane; tek kalan sonuncusu
       büyüyüp satırı doldurur. Üçü yan yana kalınca yer tabelaları
       ("Departman") kırpılıyordu. */
    .mantine-Flex-root {
      flex-wrap: wrap;
    }

    .mantine-Flex-root > * {
      flex: 1 1 calc(50% - 4px);
      min-width: 0;
      width: auto !important;
    }
  }
`;

const SubToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
`;

const StateCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  width: 110px;

  /* Dengeleyici boşluk yalnızca tek satırda anlamlı; sarınca yer israfı. */
  ${media.mobile} {
    display: none;
  }
`;

/* ── KART KİPİ (dar ekran) ───────────────────────────────────────────────── */

const CardList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Card = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:last-child {
    border-bottom: none;
  }
`;

const CardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const CardRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const CardLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
  /* Etiket satırın en fazla yarısını alsın, değer ezilmesin. */
  max-width: 45%;
`;

const CardValue = styled.div`
  font-size: 15px;
  color: #111827;
  text-align: right;
  min-width: 0;
  overflow-wrap: break-word;

  /* Değer bir rozet/şerit ise (TagBadges vb.) sağa yaslansın. */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
`;

const StyledTable = styled(Table)`
  && {
    table-layout: fixed;
    width: 100%;

    thead tr th {
      background: #e1e7fc;
      color: #000000;
      font-size: 13px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 12px 16px;
      text-align: left;
      border: none;
    }

    tbody td {
      padding: 16px 16px;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: top;
      font-size: 15px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    }

    tbody tr {
      transition: background 0.1s ease;
      &:hover {
        background-color: #f8fafc !important;
      }
    }
  }
`;
